import { LiveblocksYjsProvider } from '@liveblocks/yjs'
import { Range } from 'quill'
import QuillCursors from 'quill-cursors'
import QuillImageDropAndPaste, { ImageData } from 'quill-image-drop-and-paste'
import MagicUrl from 'quill-magic-url'
import QuillResizeImage from 'quill-resize-image'
import { Context } from 'quill/modules/keyboard'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactQuill, { Quill } from 'react-quill-new'
import { Awareness, QuillBinding } from 'y-quill'
import * as Y from 'yjs'

import { cn } from '@/lib/ui/style-helpers'

import './modules'
import { Toolbar } from './toolbar'
import { stripFormattingSpaceAfterBreak } from './utils'

import './quill-formatting.less'
import 'react-quill-new/dist/quill.snow.css'

export type QuillEditorType = Quill

// Register Quill modules
Quill.register('modules/imageDropAndPaste', QuillImageDropAndPaste, true)
Quill.register('modules/cursors', QuillCursors, true)
Quill.register('modules/imageResize', QuillResizeImage, true)
Quill.register('modules/magicUrl', MagicUrl, true)

type RichTextEditorProps = {
  yText?: Y.Text
  provider?: LiveblocksYjsProvider
  isReadOnly?: boolean
  text?: string
  onQuillReady?: (quill: Quill) => void
  onFormatApply?: (format: string) => void
}

export const RichTextEditor = ({
  yText,
  provider,
  isReadOnly = false,
  text,
  onQuillReady,
  onFormatApply,
}: RichTextEditorProps) => {
  const quillRef = useRef<ReactQuill>(null)
  const [value, setValue] = useState(text ?? '')

  // Helper function to safely get the Quill editor instance
  const getQuill = useCallback(() => {
    if (!quillRef.current) {
      return null
    }

    try {
      return quillRef.current.getEditor()
    } catch {
      return null
    }
  }, [])

  useEffect(() => {
    const quill = getQuill()
    if (!quill) {
      return
    }
    if (onQuillReady) {
      onQuillReady(quill)
    }
  }, [onQuillReady, getQuill])

  // This handles the actual binding between the editor and the yjs provider
  useEffect(() => {
    if (!quillRef.current) {
      return undefined
    }
    if (!yText) {
      return undefined
    }

    // Safely get the editor instance, handling the case where it's not yet instantiated
    // Use getQuill helper which has proper error handling
    const quill = getQuill()
    if (!quill) {
      // Editor not yet instantiated, return early
      return undefined
    }

    if (isReadOnly) {
      const binding = new QuillBinding(yText, quill)
      return () => {
        binding?.destroy?.()
      }
    }

    quill.keyboard.addBinding(
      { key: ' ' },
      { collapsed: true, prefix: /^#{1,3}?/ },
      (range: Range, context: Context) => {
        const headingSize = context.prefix.length
        if (headingSize < 1 || headingSize > 3) {
          quill.insertText(range.index, ' ')
          return
        }

        quill.deleteText(range.index - headingSize, headingSize)
        quill.format('header', headingSize)
      },
    )

    if (!provider) {
      return undefined
    }
    const binding = new QuillBinding(
      yText,
      quill,
      provider.awareness as unknown as Awareness, // This is on purpose, misalignment between types in liveblocks and yjs
    )

    return () => {
      binding?.destroy?.()
    }
  }, [yText, provider, isReadOnly, getQuill])

  // Modified image handler with proper typing
  // This handles pasting images into the editor
  const imageHandler = useCallback(
    async (_dataUrl: string, _type: string, imageData: ImageData) => {
      const quillObj = getQuill()
      const range = quillObj?.getSelection()
      if (!quillObj || !range) return

      const res: ImageData | { message: string } = await imageData.minify({
        maxWidth: 650,
        maxHeight: 650,
        quality: 0.8,
      })

      if ('message' in res) {
        console.error(res.message)
        return
      }

      const index = range.index ?? quillObj.getLength()
      quillObj.insertEmbed(index, 'image', res.dataUrl, 'user')
    },
    [getQuill],
  )

  const uploadImage = useCallback(() => {
    const quill = getQuill()
    if (!quill) {
      return
    }
    const fileInput = document.createElement('input')
    fileInput.setAttribute('type', 'file')
    fileInput.setAttribute('accept', 'image/png, image/gif, image/jpeg, image/bmp, image/x-icon')
    fileInput.classList.add('ql-image')
    fileInput.addEventListener('change', (e: Event) => {
      const files = (e.target as HTMLInputElement)?.files
      let file: File | null = null
      if (files && files.length > 0) {
        file = files[0]
        const type = file.type
        const reader = new FileReader()
        reader.onload = (e) => {
          // handle the inserted image
          const dataUrl = e.target?.result as string
          if (!dataUrl) {
            return
          }
          imageHandler(dataUrl, type, new ImageData(dataUrl, type, file?.name))
          fileInput.value = ''
        }
        reader.readAsDataURL(file)
      }
    })
    fileInput.click()
  }, [getQuill, imageHandler])

  const modules = useMemo(() => {
    // Applies to programmatic inserts and user pastes alike, so it belongs in every editor.
    const clipboard = { matchers: [[Node.TEXT_NODE, stripFormattingSpaceAfterBreak]] }

    if (isReadOnly) {
      return {
        clipboard,
        toolbar: false,
      }
    }
    return {
      clipboard,
      magicUrl: true,
      imageResize: {
        modules: ['Resize', 'DisplaySize'],
      },
      toolbar: {
        container: '#hidden-toolbar', // Point to a hidden container
        handlers: {
          image: uploadImage,
        },
      },
      history: {
        userOnly: true, // Prevent undo/redo from affecting other users
      },
      cursors: {
        transformOnTextChange: true,
      },
      imageDropAndPaste: {
        handler: imageHandler,
      },
    }
  }, [imageHandler, isReadOnly, uploadImage])

  return (
    <div
      onClick={(e) => {
        // Don't focus if clicking on tooltip input or action buttons
        if (e.target instanceof Element) {
          const target = e.target as Element
          if (target.closest('.ql-tooltip input') || target.closest('.ql-tooltip .ql-action')) {
            return
          }
        }
        const quill = getQuill()
        if (quill) {
          quill.focus()
        }
      }}
      className={cn('quill-container flex h-full px-2', {
        'rounded border border-main-border-default': !isReadOnly,
        'max-w-3xl p-0': isReadOnly,
      })}
      id='quill-editor-container'
    >
      {/* Hidden toolbar container for Quill module initialization */}
      <div id='hidden-toolbar' className='hidden' />

      <div className='mx-auto flex h-full w-full flex-col'>
        <div className='quill-editorHeader h-fit border-none'>
          {!isReadOnly && (
            <Toolbar getQuill={getQuill} uploadImage={uploadImage} onFormatApply={onFormatApply} />
          )}
        </div>
        <div className={'quill-editorContainer min-h-10 flex-1 border-0'}>
          <ReactQuill
            placeholder='Start collaborating...'
            className={cn('[&>div.ql-container]:border-0', isReadOnly && '[&_.ql-editor]:p-0')}
            readOnly={isReadOnly}
            theme='snow'
            value={value}
            onChange={setValue}
            modules={modules}
            formats={['header', 'bold', 'italic', 'underline', 'strike', 'link', 'list', 'image']}
            bounds='#quill-editor-container'
            ref={quillRef}
          />
        </div>
      </div>
    </div>
  )
}
